import React, { useState, useEffect, useRef } from 'react';
import './Clown.css';

function Clown({ theme }) {
    const [threatLevel, setThreatLevel] = useState('HIGH');
    const [clownCount, setClownCount] = useState(Math.floor(Math.random() * 1000) + 100);
    const [petitionCount, setPetitionCount] = useState(47832);
    const [reportSubmitted, setReportSubmitted] = useState(false);
    const [newsTickerIndex, setNewsTickerIndex] = useState(0);
    const audioRef = useRef(null);

    const newsTickers = [
        "BREAKING: Local clown still not funny | Experts baffled | More at 11",
        "ALERT: Clown spotted honking suspiciously | Residents urged to stay indoors",
        "UPDATE: Balloon animals continue to concern scientists worldwide",
        "DEVELOPING: Oversized shoes detected near elementary school | No further details available"
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setNewsTickerIndex((prev) => (prev + 1) % newsTickers.length);
            setClownCount(Math.floor(Math.random() * 1000) + 100);
        }, 8000);
        return () => clearInterval(interval);
    }, []);

    const handleHonk = () => {
        if (audioRef.current) {
            audioRef.current.play().catch(() => { });
        }
        // Shake effect
        document.body.classList.add('shake');
        setTimeout(() => document.body.classList.remove('shake'), 500);
    };

    const handleReportClown = (e) => {
        e.preventDefault();
        setReportSubmitted(true);
        setTimeout(() => setReportSubmitted(false), 3000);
    };

    const handleThreatCalculator = (e) => {
        e.preventDefault();
        setThreatLevel('CRITICALLY HIGH - EVACUATE IMMEDIATELY');
    };

    const handlePetition = () => {
        setPetitionCount(prev => prev + Math.floor(Math.random() * 5) + 1);
    };

    return (
        <div className={`clown-page ${theme}`}>
            <audio ref={audioRef} src="/dragon-studio-clown-car-horn-487659.mp3" />

            {/* Breaking News Ticker */}
            <div className="news-ticker">
                <span className="ticker-label">BREAKING NEWS:</span>
                <div className="ticker-content">
                    {newsTickers[newsTickerIndex]}
                </div>
            </div>

            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-content">
                    <h1 className="hero-title">STUPID CLOWNS: A THREAT TO SOCIETY</h1>
                    <p className="hero-subtitle">Courtesy: Vibe coding agents. Not a single line of code in this page was written by me.</p>
                    <p>But I am planning on adding jump-scares in this page soon.</p>
                    <div className="danger-rating">
                        DANGER LEVEL: <span className="clown-emojis">🤡🤡🤡🤡🤡</span> out of 5 clowns
                    </div>
                </div>
                <div className="hero-image">
                    <img src="/clown.jpg" alt="Artist's impression of the enemy" />
                    <p className="image-caption">Artist's impression of the enemy</p>
                </div>
            </section>

            {/* Statistics Section */}
            <section className="stats-section">
                <h2>The Troubling Statistics</h2>
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-number">87.4%</div>
                        <div className="stat-label">of clowns have never once been funny</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">14.7</div>
                        <div className="stat-label">average pairs of oversized shoes per clown</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">+340%</div>
                        <div className="stat-label">clown car incidents since 1987</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">0</div>
                        <div className="stat-label">peer reviewed studies confirming honking is helpful</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">9/10</div>
                        <div className="stat-label">balloons regret being twisted into animals</div>
                    </div>
                </div>
                <p className="source-citation">Source: Journal of Clownological Studies, Vol. 4, Issue 🤡</p>
            </section>

            {/* History Section */}
            <section className="history-section">
                <h2>Timeline of Clown Incidents</h2>
                <div className="timeline">
                    <div className="timeline-item">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                            <h3>3000 BC</h3>
                            <p>First clown detected. Historians deeply upset.</p>
                        </div>
                    </div>
                    <div className="timeline-item">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                            <h3>1347</h3>
                            <p>The Black Plague. Clowns suspected. Never proven.</p>
                        </div>
                    </div>
                    <div className="timeline-item">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                            <h3>1888</h3>
                            <p>Jack the Ripper. Clown alibi never confirmed.</p>
                        </div>
                    </div>
                    <div className="timeline-item">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                            <h3>1929</h3>
                            <p>Stock market crash. A clown was seen nearby.</p>
                        </div>
                    </div>
                    <div className="timeline-item">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                            <h3>2016</h3>
                            <p>The Clown Sightings Incident. They were testing something.</p>
                        </div>
                    </div>
                    <div className="timeline-item">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                            <h3>Today</h3>
                            <p>They are still out there. Honking.</p>
                        </div>
                    </div>
                </div>
                <div className="did-you-know">
                    <strong>Did You Know?</strong> The word 'clown' has no known innocent origin. Scholars are concerned.
                </div>
            </section>

            {/* Taxonomy Section */}
            <section className="taxonomy-section">
                <h2>Know Your Enemy: A Field Guide To Clown Types</h2>
                <div className="taxonomy-grid">
                    <div className="taxonomy-card">
                        <h3>The Birthday Clown</h3>
                        <p>Traumatizes children for minimum wage.</p>
                        <div className="threat-level">Threat: <span>🤡🤡</span></div>
                    </div>
                    <div className="taxonomy-card">
                        <h3>The Rodeo Clown</h3>
                        <p>Inexplicably trusted with bull distraction.</p>
                        <div className="threat-level">Threat: <span>🤡🤡🤡</span></div>
                    </div>
                    <div className="taxonomy-card">
                        <h3>The IT Clown</h3>
                        <p>Lives in drains. Has a balloon. No further questions.</p>
                        <div className="threat-level">Threat: <span>🤡🤡🤡🤡🤡</span></div>
                    </div>
                    <div className="taxonomy-card">
                        <h3>The Mime</h3>
                        <p>Technically not a clown but the vibes are identical.</p>
                        <div className="threat-level">Threat: <span>😰😰😰</span></div>
                    </div>
                    <div className="taxonomy-card">
                        <h3>The Corporate Clown</h3>
                        <p>Wears a suit. Schedules unnecessary meetings. Most dangerous of all.</p>
                        <div className="threat-level">Threat: <span>🤡🤡🤡🤡🤡</span></div>
                    </div>
                    <div className="taxonomy-card">
                        <h3>The Politician</h3>
                        <p>See: Corporate Clown.</p>
                        <div className="threat-level">Threat: <span>⚠️⚠️⚠️⚠️⚠️</span></div>
                    </div>
                </div>
            </section>

            {/* Science Section */}
            <section className="science-section">
                <h2>Why Are Clowns Like This: A Scientific Inquiry</h2>
                <div className="science-content">
                    <div className="clown-diagram">
                        <img src="/clown.jpg" alt="Clown anatomy diagram" className="diagram-image" />
                        <div className="diagram-label label-1">Red nose: Purpose unknown. Concerning.</div>
                        <div className="diagram-label label-2">Giant shoes: Biomechanically impossible. Red flag.</div>
                        <div className="diagram-label label-3">Flower: Confirmed water delivery system. Threat vector.</div>
                        <div className="diagram-label label-4">Pants: What are they hiding. Investigate.</div>
                        <div className="diagram-label label-5">Smile: Painted on. Trust nothing.</div>
                    </div>
                    <div className="research-abstract">
                        <h3>Abstract</h3>
                        <p>This paper investigates the phenomenological manifestations of clown-adjacent behavioral patterns through a post-structuralist lens, applying quantum mechanics to the inherent honkness quotient while simultaneously deconstructing the ontological status of oversized footwear. Our findings suggest that further study is impossible because the clowns are watching us.</p>
                        <p className="peer-review">Peer Reviewed by: Dr. Honksworth, Professor of Clownology, University of Made Up</p>
                    </div>
                </div>
            </section>

            {/* Survival Guide Section */}
            <section className="survival-section">
                <h2>Survival Guide: What To Do If You Encounter A Clown</h2>
                <div className="survival-steps">
                    <div className="step">
                        <div className="step-number">1</div>
                        <div className="step-content">
                            <h3>Do not make eye contact.</h3>
                            <p>They can sense fear and also your location.</p>
                        </div>
                    </div>
                    <div className="step">
                        <div className="step-number">2</div>
                        <div className="step-content">
                            <h3>Do not accept a balloon animal.</h3>
                            <p>It is a contract.</p>
                        </div>
                    </div>
                    <div className="step">
                        <div className="step-number">3</div>
                        <div className="step-content">
                            <h3>If they offer you a flower from their lapel, run.</h3>
                            <p>That is a weapon.</p>
                        </div>
                    </div>
                    <div className="step">
                        <div className="step-number">4</div>
                        <div className="step-content">
                            <h3>Never get in the car.</h3>
                            <p>You know why.</p>
                        </div>
                    </div>
                    <div className="step">
                        <div className="step-number">5</div>
                        <div className="step-content">
                            <h3>Laughter is their power source.</h3>
                            <p>Remain stone faced at all costs.</p>
                        </div>
                    </div>
                    <div className="step">
                        <div className="step-number">6</div>
                        <div className="step-content">
                            <h3>Ask them to explain the joke.</h3>
                            <p>They will short circuit.</p>
                        </div>
                    </div>
                    <div className="step">
                        <div className="step-number">7</div>
                        <div className="step-content">
                            <h3>In case of emergency, honk back.</h3>
                            <p>Establish dominance.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="testimonials-section">
                <h2>Testimonials From The Concerned</h2>
                <div className="testimonials-grid">
                    <div className="testimonial-card">
                        <p className="testimonial-text">"I hired a clown for my son's birthday. My son now has a law degree specializing in clown litigation."</p>
                        <p className="testimonial-author">— Karen M., Ohio</p>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">"I looked a clown directly in the eyes. I have not slept since."</p>
                        <p className="testimonial-author">— Dave T., Somewhere Bad</p>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">"The balloon animal he made me was a dog. It was not a dog."</p>
                        <p className="testimonial-author">— Anonymous</p>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">"I asked the clown to leave. He said 'this is my house now.' It was my house."</p>
                        <p className="testimonial-author">— Still Unresolved</p>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">"Four stars. Minus one star because of the honking."</p>
                        <p className="testimonial-author">— Someone Who Gave A Clown Four Stars</p>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="faq-section">
                <h2>Frequently Asked Questions Nobody Asked</h2>
                <div className="faq-grid">
                    <div className="faq-item">
                        <h3>Q: Are clowns dangerous?</h3>
                        <p>A: Statistically unconfirmed. Spiritually, yes.</p>
                    </div>
                    <div className="faq-item">
                        <h3>Q: Why do clowns wear makeup?</h3>
                        <p>A: To hide what's underneath. We don't ask follow up questions.</p>
                    </div>
                    <div className="faq-item">
                        <h3>Q: How many clowns fit in a car?</h3>
                        <p>A: More than is reasonable. Always more than is reasonable.</p>
                    </div>
                    <div className="faq-item">
                        <h3>Q: Is Pennywise a clown?</h3>
                        <p>A: Pennywise is what happens when a clown achieves its final form.</p>
                    </div>
                    <div className="faq-item">
                        <h3>Q: Can clowns be trusted?</h3>
                        <p>A: This website exists because someone asked that question once. Look where we are.</p>
                    </div>
                    <div className="faq-item">
                        <h3>Q: Should I hire a clown for my child's birthday?</h3>
                        <p>A: This FAQ cannot provide legal advice.</p>
                    </div>
                </div>
            </section>

            {/* Interactive Elements */}
            <section className="interactive-section">
                <h2>Interactive Tools</h2>

                <div className="tool-card">
                    <h3>Emergency Honk Button</h3>
                    <p>Use in case of imminent clown attack.</p>
                    <button className="honk-button" onClick={handleHonk}>
                        🔔 HONK! 🔔
                    </button>
                </div>

                <div className="tool-card">
                    <h3>Clown Threat Level Calculator</h3>
                    <form onSubmit={handleThreatCalculator}>
                        <input type="text" placeholder="Enter your location..." required />
                        <button type="submit">Calculate Threat</button>
                        {threatLevel !== 'HIGH' && threatLevel !== 'HIGH THREAT' && (
                            <div className="threat-result">{threatLevel}</div>
                        )}
                        {threatLevel === 'CRITICALLY HIGH - EVACUATE IMMEDIATELY' && (
                            <div className="threat-result critical">
                                {threatLevel}
                            </div>
                        )}
                    </form>
                </div>

                <div className="tool-card">
                    <h3>Report A Clown</h3>
                    <form onSubmit={handleReportClown}>
                        <input type="text" placeholder="Clown location..." required />
                        <input type="text" placeholder="Description..." required />
                        <button type="submit">Submit Report</button>
                        {reportSubmitted && (
                            <div className="report-response">
                                ✓ Thank you. Authorities have been notified. (They have not.)
                            </div>
                        )}
                    </form>
                </div>

                <div className="tool-card">
                    <h3>Live Clown Counter</h3>
                    <div className="counter-display">
                        Clowns currently operating in your area: <span className="counter-number">{clownCount}</span>
                    </div>
                    <p className="counter-note">Updates every 8 seconds. Probably.</p>
                </div>

                <div className="tool-card">
                    <h3>Ban Clowns From Everything Petition</h3>
                    <div className="petition-info">
                        <div className="signature-count">{petitionCount.toLocaleString()} signatures</div>
                        <button className="petition-button" onClick={handlePetition}>Sign Petition</button>
                        <p className="petition-note">You are now on a list. So are the clowns.</p>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="clown-footer">
                <p>This website is not affiliated with any clown organization. Any resemblance to actual clowns living or honking is purely coincidental.</p>
                <p>No clowns were harmed in the making of this website. Several were mildly inconvenienced.</p>
                <p>© 2026 The Institute for Clown Awareness. All rights reserved. Honk responsibly.</p>
            </footer>
        </div>
    );
}

export default Clown;
